import { useLocale, useTranslations } from 'next-intl'
import React, { useCallback, useMemo } from 'react'

import DashboardLoadingIcon from '@/components/icon/dashboard-loading-icon'
import { NotificationItemDisplay } from '@/components/notification/notification-item-display'
import { Skeleton } from '@/components/ui/skeleton'

import { useInfiniteNotificationsQuery } from '@/hooks/query/notification'
import { useDebounce } from '@/hooks/use-debounce'
import { useInfiniteScrollFetch } from '@/hooks/use-infinite-scroll-fetch'
import { DEFAULT_FETCH_DEBOUNCE } from '@/lib/constants'
import { getNotificationTypeFromGroup } from '@/lib/notification'
import { cn } from '@/lib/utils'
import { AvailableLocale } from '@/types/global'
import { NotificationsFetchFilterApi, NotificationsFilterClient } from '@/types/notification'

type NotificationListProps = {
  className?: string
  filter?: NotificationsFilterClient
  onNotificationDisplayClick?: () => void
}

const NotificationList = ({ className, filter, onNotificationDisplayClick }: NotificationListProps) => {
  const tComponent = useTranslations('Notification.components.notification_list')
  const locale = useLocale() as AvailableLocale

  const notificationFilter = useMemo<NotificationsFetchFilterApi>(() => {
    return {
      types: filter?.groupType?.flatMap((group) => getNotificationTypeFromGroup(group)) || [],
      isRead: !filter?.isUnreadOnly,
      keyword: filter?.keyword || '',
      sortBy: 'createdAt',
      sortOrder: 'desc',
    }
  }, [filter])

  const infiniteNotificationsQueryParameterValue = useMemo(() => {
    return {
      locale,
      filter: notificationFilter,
    }
  }, [locale, notificationFilter])

  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } = useInfiniteNotificationsQuery(
    infiniteNotificationsQueryParameterValue,
  )

  const setObserverRef = useInfiniteScrollFetch({
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  })

  const hasNoData = useMemo(() => {
    return !data || data.pages.length === 0 || data.pages.every((page) => page.items.length === 0)
  }, [data])

  const shouldShowSkeletonLoading = useMemo(() => {
    return isFetching && hasNoData
  }, [isFetching, hasNoData])

  // use to avoid flickering when there is no data
  // and skeleton loading is shown and there is no data again after fetch
  const debouncedShouldShowSkeleton = useDebounce(shouldShowSkeletonLoading, DEFAULT_FETCH_DEBOUNCE)

  const handleNotificationDisplayClick = useCallback(() => {
    onNotificationDisplayClick?.()
  }, [onNotificationDisplayClick])

  return (
    <div className={cn('flex h-full flex-col overflow-y-auto', className)}>
      {debouncedShouldShowSkeleton ? (
        <div className="mt-4 flex animate-fade-in flex-col gap-4 px-4">
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
          <Skeleton className="h-12" />
        </div>
      ) : hasNoData ? (
        <p className="my-4 h-full content-center text-center text-sm text-muted-foreground">
          {filter?.isUnreadOnly ? tComponent('messages.no_data_unread_only') : tComponent('messages.no_data')}
        </p>
      ) : (
        <>
          {data?.pages?.map((group, i) => {
            return (
              <React.Fragment key={i}>
                {group.items.map((item) => {
                  return (
                    <NotificationItemDisplay
                      key={item._id}
                      item={item}
                      className="animate-fade-in"
                      onClick={handleNotificationDisplayClick}
                    />
                  )
                })}
              </React.Fragment>
            )
          })}

          {isFetchingNextPage && (
            <div className="flex items-center justify-center py-6">
              <DashboardLoadingIcon />
            </div>
          )}
          <div ref={setObserverRef} className="h-8 w-full" aria-label="Loading sentinel" aria-hidden="true" />
        </>
      )}
    </div>
  )
}
NotificationList.displayName = 'NotificationList'

export { NotificationList }
